import { useState, useCallback } from 'react';
import { useMedplum } from '@medplum/react';
import { Communication, Practitioner } from '@medplum/fhirtypes';

// -----------------------------------------------------------------------------
interface CommunicationWithPractitioner extends Communication {
  practitioner?: Practitioner;
}

// -----------------------------------------------------------------------------
export const useCommunications = (reportId?: string) => {
  const medplum = useMedplum();
  const [communications, setCommunications] = useState<
    CommunicationWithPractitioner[]
  >([]);
  const [communicationsLoading, setCommunicationsLoading] = useState(false);

  // ---------------------------------------------------------------------------
  const loadCommunications = useCallback(async () => {
    if (!reportId) {
      return;
    }

    setCommunicationsLoading(true);
    try {
      // Search for communications that reference this diagnostic report
      const comms = await medplum.searchResources('Communication', {
        'based-on': `DiagnosticReport/${reportId}`,
        _sort: '-sent',
      });

      // Load practitioner information for each communication
      const communicationsWithPractitioners = await Promise.all(
        comms.map(async (comm) => {
          let practitioner: Practitioner | undefined;

          // Try to get practitioner from sender first, then recipient
          const senderRef = comm.sender?.reference;
          const recipientRef = comm.recipient?.[0]?.reference;

          const practitionerRef = senderRef || recipientRef;

          if (practitionerRef?.startsWith('Practitioner/')) {
            const practitionerId = practitionerRef.replace('Practitioner/', '');
            try {
              practitioner = await medplum.readResource(
                'Practitioner',
                practitionerId
              );
            } catch (error) {
              console.warn('Could not load practitioner:', error);
            }
          }

          return {
            ...comm,
            practitioner,
          };
        })
      );

      setCommunications(communicationsWithPractitioners);
    } catch (error) {
      console.error('Error loading communications:', error);
    } finally {
      setCommunicationsLoading(false);
    }
  }, [reportId, medplum]);

  // ---------------------------------------------------------------------------
  return {
    communications,
    communicationsLoading,
    loadCommunications,
  };
};
